import React, { useState, memo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import {
  StarBorderRounded as StarIcon,
  StarRounded as BookmarkedIcon,
  FileCopyRounded as FileIcon,
  GetApp as SaveIcon,
} from '@material-ui/icons'
import { useTheme } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { setConversations } from '../../../redux/conversations/actions'
import { setGalleryOpen } from '../../../redux/gallery/actions'

import messages from '../../../services/messages'
import Typography from '../display/typography'
import Highlight from '../display/highlight'
import HighlightSearch from '../display/highlightSearch'
import IconButton from '../inputs/iconButton'
import AudioPlayer from '../feedback/audioPlayer'
import CircularProgress from '../feedback/circularProgress'

import useStyles from './balloonConsumerStyle'
import handleDownload from '../../../utils/downloadFile'

const BalloonConsumer = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const size = useSelector((state) => state.preferences)
  const theme = useTheme()
  let useStylesParams = {
    size: size,
    resize: props?.resize,
  }
  const classes = useStyles(useStylesParams)

  const activeConversation = useSelector((state) => state.activeConversation)
  const conversation = useSelector(
    (state) => state.conversations.conversations[activeConversation]
  )
  const messageStore = useSelector(
    (state) => state.conversations.messages[activeConversation]
  )
  const chatKeyword = useSelector((state) => state.keyword)
  const [showActions, setShowActions] = useState(false)
  const [bookmarked, setBookmarked] = useState(props.data.favorite)
  const [loading, setLoading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const copyToClippboard = (string) => {
    navigator.clipboard.writeText(string).then(
      () => {
        enqueueSnackbar(`${t('commons.copied')}`, {
          variant: 'success',
        })
      },
      (err) => {
        console.error(t('commons.copiedError'), err)
      }
    )
  }

  const handleFavorite = () => {
    setLoading(true)
    messages({
      method: 'patch',
      url: `/tech4h-messages/message/favorite/${props.data.currentId}`,
      data: {
        favorite: bookmarked ? false : true,
      },
    })
      .then((response) => {
        setLoading(false)
        setBookmarked(bookmarked ? false : true)
        let msgs = messageStore
        for (let i = 0; i < msgs.length; i++) {
          if (msgs[i].currentId === props?.data?.currentId) {
            msgs[i].favorite = !bookmarked
            break
          }
        }
        conversation.message = msgs
        dispatch(setConversations(conversation))
      })
      .catch((error) => {})
  }

  const renderActions = () => {
    if (props.showBookmark && showActions && props.data.currentId) {
      return (
        <div className={classes.actions}>
          <IconButton
            size="small"
            color="primary"
            disabled={loading}
            component="span"
            style={{ width: 32, height: 32 }}
            onClick={() => handleFavorite()}
          >
            {bookmarked ? (
              loading ? (
                <CircularProgress size={16} />
              ) : (
                <BookmarkedIcon fontSize="inherit" />
              )
            ) : loading ? (
              <CircularProgress size={16} />
            ) : (
              <StarIcon fontSize="inherit" />
            )}
          </IconButton>
          {props?.data?.files ? (
            <IconButton
              size="small"
              color="primary"
              component="span"
              style={{ width: 32, height: 32, marginTop: 5 }}
              onClick={() =>
                handleDownload(
                  props.data.files.file_url,
                  props.data.files.file_name
                )
              }
            >
              <SaveIcon
                fontSize="inherit"
                style={{ transform: 'scaleX(-1)' }}
              />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              color="primary"
              component="span"
              style={{ width: 32, height: 32, marginTop: 5 }}
              onClick={(e) => {
                e.stopPropagation()
                copyToClippboard(props.data.text)
              }}
            >
              <FileIcon
                fontSize="inherit"
                style={{ transform: 'scaleX(-1)' }}
              />
            </IconButton>
          )}
          {/* <IconButton
            size="small"
            color="primary"
            component="span"
            style={{ width: 32, height: 32, marginTop: 5 }}
          >
            <RepplyIcon
              fontSize="inherit"
              style={{ transform: 'scaleX(-1)' }}
            />
          </IconButton> */}
        </div>
      )
    } else {
      return <></>
    }
  }

  const renderBookmarked = () => {
    if (bookmarked && !showActions) {
      return (
        <div className={classes.bookmarked} id="bookmarked">
          <BookmarkedIcon
            fontSize="inherit"
            style={{
              fontSize: 18,
              color: theme.palette.primary.main,
            }}
          />
        </div>
      )
    } else {
      return <></>
    }
  }

  const renderPicture = () => {
    if (props?.data?.files) {
      if (
        props.data.files.mime_type?.split('/')[0] !== 'audio' &&
        props.data.files.file_url
      ) {
        return (
          <>
            <div className={'anchor-midia'} style={{ padding: 0 }}>
              {props.data.files.mime_type?.split('/')[0] === 'image' ? (
                <img
                  className={classes.picture}
                  src={props.data.files.thumb_url}
                  alt=""
                  onClick={() =>
                    dispatch(setGalleryOpen(true, props.data.files.file_url))
                  }
                />
              ) : (
                <a href={props.data.files.file_url} target="_blank">
                  <img
                    className={classes.picture}
                    src={props.data.files.thumb_url}
                    alt=""
                  />
                </a>
              )}
            </div>
          </>
        )
      }
    }
  }

  const renderAudio = () => {
    if (props?.data?.files) {
      if (
        props.data.files.mime_type?.split('/')[0] === 'audio' &&
        props.data.files.file_url
      ) {
        return (
          <>
            <div className={'anchor-audio'} style={{ marginTop: -15 }}>
              <AudioPlayer
                activeConversation={activeConversation}
                src={props.data.files.file_url}
                type={props.data.files.mime_type?.split(';')[0]}
              />
            </div>
          </>
        )
      }
    }
  }

  const renderLocation = () => {
    if (props?.data?.location) {
      return (
        <>
          <div className={'anchor-location'} style={{ marginTop: -15 }}>
            <a
              href={props.data.location.location_url}
              target="_blank"
              rel="noreferrer"
            >
              <div className={classes.location}></div>
            </a>
            <Typography variant="body2">{t('commons.openLocation')}</Typography>
          </div>
        </>
      )
    }
  }

  const renderHeader = () => {
    return (
      <Typography variant="body1" component="h4">
        {props?.consumer?.name ?? conversation?.consumer?.name}
        <span
          style={{
            marginLeft: 6,
            color:
              theme.palette.type !== 'dark' ? '#fff' : theme.palette.status[4],
          }}
        >
          {moment(props.data.sentTime).format('HH:mm')}
        </span>
      </Typography>
    )
  }

  const renderText = () => {
    if (props.data.text) {
      if (chatKeyword) {
        return (
          <>
            <HighlightSearch
              text={props.data.text}
              keyword={chatKeyword}
              anchor={props.anchor ? props.anchor : 'chat'}
              copy={false}
            />
          </>
        )
      } else {
        return <Highlight text={props.data.text} />
      }
    }
  }

  return (
    <>
      <div
        id={props.data.currentId}
        className={classes.root}
        onClick={props.onClick}
        style={props.style}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        <div className={classes.child}>
          {renderHeader()}
          <Typography
            variant="body1"
            component="p"
            style={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
          >
            <span className={'escapeDragScroll'} style={{ cursor: 'text' }}>
              {renderText()}
            </span>
          </Typography>
          {renderPicture()}
          {renderAudio()}
          {renderLocation()}
        </div>
        {renderActions()}
        {props.showBookmark && renderBookmarked()}
      </div>
    </>
  )
}

export default memo(BalloonConsumer)
